import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { pointerIconPng, pointerIconXsPng } from '../../assets/images';
import { YouTubeVideo } from '../../components/YouTubeVideo';
import { useConfig } from '../../hooks/use-config';
import { userAtom } from '../../jotai-atoms/user.atom';
import { User } from '../../models/User';
import { PlatformTourService } from '../../services/platform-tour';
import { Button } from '../../stories/Button';
import { Header } from '../../stories/Header';
import { VideoModal } from './VideoModal';

export const PlatformTourVideoPage: React.FC = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);
  const [isVideoWatched, setIsVideoWatched] = useState<boolean>(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);
  const [user, setUser] = useAtom(userAtom);
  const { config } = useConfig();

  const __ = useTranslation().t;

  const buttonLabel = __('returnToMainPage');
  const youtubeVideoId = config.platformTourVideoId;

  const onVideoEnd = () => {
    setIsVideoWatched(true);
  };

  useEffect(() => {
    if (!isVideoWatched) {
      return;
    }

    PlatformTourService.postPlatformTourVideoWatched()
      .then((response) => {
        if (response.data.success) {
          setIsButtonEnabled(true);
          setUser({
            ...user,
            platformTourVideoPlayerUrl: null,
          } as User);
        } else {
          alert(__('whopsSomethingWentWrong'));
        }
      })
      .catch(() => {
        alert(__('whopsSomethingWentWrong'));
      });
  }, [isVideoWatched]);

  const onButtonClick = () => {
    window.location.href = `/`;
  };

  return (
    <>
      <Header type="logo-only" />
      <Background>
        <MainDiv>
          <Container>
            <Trans alt={__('welcomeToPlatformTour')} i18nKey="welcomeToPlatformTour">
              <StyledH1 color="blue">Welcome!</StyledH1>
              <StyledH1 color="orange">Start your learning journey with the best tips</StyledH1>
            </Trans>
            <MainDescription className="pt-video-description">
              {__('completeTourReturn')}
            </MainDescription>
            <Button
              label={buttonLabel}
              isPulsing={isButtonEnabled}
              disabled={!isButtonEnabled}
              onClick={onButtonClick}
            />
            <YouTubeContainer>
              <StyledMessage>
                <Trans
                  alt={__('watchVideoEnableLiveClasses')}
                  i18nKey="watchVideoEnableLiveClasses"
                >
                  <StyledSpan color="blue">Watch the video to</StyledSpan>
                  <StyledSpan color="blue">enable your live classes</StyledSpan>
                </Trans>
              </StyledMessage>
              <StyledPointerImg src={pointerIconPng} alt="Pointer" className="d-none d-lg-block" />
              <StyledPointerImg
                src={pointerIconXsPng}
                alt="Pointer"
                className="d-block d-lg-none"
              />
              <MobileEventCatcher
                onClick={() => setIsVideoModalOpen(true)}
                className="d-block d-lg-none"
              />
              <EmbedContainer className="embed-container">
                <YouTubeVideo
                  autoplayEnabled={false}
                  videoId={youtubeVideoId}
                  onEnd={() => onVideoEnd()}
                />
              </EmbedContainer>
            </YouTubeContainer>
          </Container>
        </MainDiv>
        <Footer>Copyright © {new Date().getFullYear()} Open Education LLC.</Footer>
      </Background>
      <VideoModal
        show={isVideoModalOpen}
        setShow={setIsVideoModalOpen}
        setEnded={() => onVideoEnd()}
        youtubeVideoId={youtubeVideoId}
      />
    </>
  );
};

const Background = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
`;

const MainDiv = styled.div`
  padding-top: 64px;
  padding-bottom: 257px;
  background-color: ${({ theme }) => theme.colors.backgroundSuperLight};
  text-align: center;
  position: relative;
  z-index: 1;
  margin-bottom: 300px;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    padding-top: 24px;
    padding-bottom: 32px;
    margin-bottom: 0;
  }
`;

const MainDescription = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.black};
  max-width: 637px;
  margin: 12px auto 24px;
`;

interface StyledH1Props {
  color: 'blue' | 'orange';
}

const StyledH1 = styled.h1<StyledH1Props>`
  font-family: ${({ theme }) => theme.fonts.primary.bold.fontFamily};
  font-weight: 700;
  font-size: 32px !important;
  line-height: 40px;
  color: ${({ theme, color }) => (color === 'blue' ? theme.colors.blue : theme.colors.orange)};

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.md}) {
    font-size: 24px !important;
    line-height: 32px;
  }
`;

const Footer = styled.div`
  padding: 26px 0;
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.black};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  text-align: center;
`;

const YouTubeContainer = styled.div`
  width: 719px;
  height: 363px;
  border-radius: 12px;
  position: absolute;
  transform: translate(-50%, 60px);
  left: 50%;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    width: 100%;
    height: auto;
    position: relative;
    transform: none;
    left: 0;
    margin-bottom: 32px;
    margin-top: 120px;
  }
`;

const StyledMessage = styled.div`
  position: absolute;
  left: -50%;
  transform: translateX(50%);
  top: -105px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    right: 40px;
    left: initial;
    top: -87px;
    transform: none;
    max-width: 260px;
  }

  @media screen and (min-width: ${({ theme }) => theme.screenSizes.lg}) and (max-width: ${({
      theme,
    }) => theme.screenSizes.xl}) {
    max-width: 280px;
    top: -110px;
    left: -38%;
  }
`;

const StyledSpan = styled.span`
  font-family: 'Gochi Hand', cursive;
  font-size: 24px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.blue};
`;

const StyledPointerImg = styled.img`
  position: absolute;
  width: 71px;
  height: 92px;
  left: -83px;
  top: -49px;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    width: 46px;
    height: 57px;
    right: 14px;
    left: initial;
    top: -67px;
  }
`;

const EmbedContainer = styled.div`
  border-radius: 12px;
`;

const MobileEventCatcher = styled.div`
  background: transparent;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
`;
